import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  X, Send, Trash2, Copy, Check,
  Calendar, FileText, Cpu, HelpCircle, ArrowRight,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const CHATBOT_ICON = '/icons/chatbot.webp';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Inline markdown renderer ──────────────────────────────────────────────────
function MdLine({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (/^\*\*[^*]+\*\*$/.test(part))
          return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
        if (/^\*[^*]+\*$/.test(part))
          return <em key={i}>{part.slice(1, -1)}</em>;
        if (/^`[^`]+`$/.test(part))
          return (
            <code key={i} className="bg-white/10 rounded px-1 text-[0.82em] font-mono">
              {part.slice(1, -1)}
            </code>
          );
        return part;
      })}
    </>
  );
}

function MarkdownContent({ content }) {
  const lines = content.split('\n');
  const nodes = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    if (/^[-•*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-•*] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-•*] /, '')); i++;
      }
      nodes.push(
        <ul key={`ul-${i}`} className="mt-1.5 space-y-1">
          {items.map((it, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="text-primary/80 mt-px flex-shrink-0">›</span>
              <span><MdLine text={it} /></span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, '')); i++;
      }
      nodes.push(
        <ol key={`ol-${i}`} className="mt-1.5 space-y-1">
          {items.map((it, idx) => (
            <li key={idx} className="flex gap-2">
              <span className="text-primary/80 font-semibold flex-shrink-0">{idx + 1}.</span>
              <span><MdLine text={it} /></span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    nodes.push(
      <p key={`p-${i}`} className={nodes.length > 0 ? 'mt-1.5' : ''}>
        <MdLine text={line} dark />
      </p>
    );
    i++;
  }

  return <div className="text-[0.8125rem] leading-relaxed">{nodes}</div>;
}

// ── Quick prompts ─────────────────────────────────────────────────────────────
const QUICK_EN = [
  { icon: FileText,    label: 'How does BoQ conversion work?' },
  { icon: Cpu,         label: 'What are Technical Submittals?' },
  { icon: Calendar,    label: 'Book a demo' },
  { icon: HelpCircle,  label: 'Which ERP systems do you support?' },
];
const QUICK_AR = [
  { icon: FileText,    label: 'كيف يعمل تحويل قائمة الكميات؟' },
  { icon: Cpu,         label: 'ما هي المستندات الفنية؟' },
  { icon: Calendar,    label: 'احجز عرضاً تجريبياً' },
  { icon: HelpCircle,  label: 'ما أنظمة ERP المدعومة؟' },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function AIAssistant({ source = 'landing' }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang  = i18n.language;

  const [isOpen,       setIsOpen]       = useState(false);
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState('');
  const [isLoading,    setIsLoading]    = useState(false);
  const [hasUnread,    setHasUnread]    = useState(false);
  const [copied,       setCopied]       = useState(null);
  const [leadEmail,    setLeadEmail]    = useState('');
  const [leadSaving,   setLeadSaving]   = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [showLead,     setShowLead]     = useState(false);

  const [sessionId] = useState(() => {
    const stored = sessionStorage.getItem('tuly_chat_session');
    if (stored) return stored;
    const newId = uuidv4();
    sessionStorage.setItem('tuly_chat_session', newId);
    return newId;
  });

  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { if (isOpen) scrollToBottom(); }, [messages, isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: t('assistant.welcomeMsg'),
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length, t]);

  // Trigger lead capture after 4 AI replies
  useEffect(() => {
    const aiCount = messages.filter(m => m.role === 'assistant' && m.id !== 'welcome').length;
    if (aiCount >= 4 && !leadCaptured && !showLead) {
      setShowLead(true);
    }
  }, [messages, leadCaptured, showLead]);

  const sendMessage = async (override) => {
    const text = (override ?? input).trim();
    if (!text || isLoading) return;

    const userMsg = { id: uuidv4(), role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, language: lang, source }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();

      setMessages(prev => [...prev, {
        id: uuidv4(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
      }]);

      if (!isOpen) setHasUnread(true);
    } catch {
      setMessages(prev => [...prev, {
        id: uuidv4(),
        role: 'assistant',
        content: lang === 'ar'
          ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.'
          : 'Sorry, something went wrong. Please try again.',
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([]);
    setShowLead(false);
    setLeadCaptured(false);
    sessionStorage.removeItem('tuly_chat_session');
  };

  const copyMessage = (id, content) => {
    navigator.clipboard.writeText(content);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const submitLead = async () => {
    if (!leadEmail.trim() || leadSaving) return;
    setLeadSaving(true);
    try {
      await fetch(`${API_BASE}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, email: leadEmail }),
      });
      setLeadCaptured(true);
      setShowLead(false);
      setMessages(prev => [...prev, {
        id: uuidv4(),
        role: 'assistant',
        content: lang === 'ar'
          ? 'شكراً! سيتواصل معك فريقنا قريباً. 🎉'
          : "Thanks! Our team will reach out to you soon. 🎉",
        timestamp: new Date(),
      }]);
    } catch { /* silent */ } finally {
      setLeadSaving(false);
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString(lang === 'ar' ? 'ar-AE' : 'en-US', {
      hour: '2-digit', minute: '2-digit',
    });

  const quickPrompts = lang === 'ar' ? QUICK_AR : QUICK_EN;

  return (
    <>
      {/* ── Floating Trigger ── */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
            className={`fixed bottom-6 z-50 group ${isRTL ? 'left-6' : 'right-6'}`}
          >
            {/* Tooltip */}
            <div className={`absolute bottom-full mb-2.5 ${isRTL ? 'left-0' : 'right-0'} whitespace-nowrap bg-[#111827] text-white text-xs font-medium px-3 py-1.5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-xl border border-white/8`}>
              {lang === 'ar' ? 'تحدث مع Tuly AI' : 'Chat with Tuly AI'}
            </div>

            <button
              onClick={() => { setIsOpen(true); setHasUnread(false); }}
              aria-label="Open Tuly AI"
              className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-primary to-[#3A66FF] flex items-center justify-center hover:scale-105 active:scale-95 transition-transform shadow-[0_0_24px_rgba(37,99,235,0.5)] overflow-hidden"
            >
              {/* Pulse ring */}
              <motion.span
                className="absolute inset-0 rounded-2xl bg-primary/30"
                animate={{ scale: [1, 1.35], opacity: [0.5, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
              />
              <motion.img
                src={CHATBOT_ICON}
                alt="Tuly AI"
                className="w-9 h-9 sm:w-10 sm:h-10 object-contain relative z-10"
                animate={{ y: [0, -3, 0], rotate: [0, -4, 4, -2, 2, 0] }}
                transition={{
                  y:      { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
                  rotate: { duration: 2.4, repeat: Infinity, repeatDelay: 4, ease: 'easeInOut' },
                }}
              />

              {hasUnread && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full border-2 border-white z-20" />
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Chat Panel ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className={`fixed bottom-6 z-50 flex flex-col w-[370px] sm:w-[400px] rounded-2xl overflow-hidden border border-slate-200 ${isRTL ? 'left-6' : 'right-6'}`}
            style={{
              maxHeight: '580px',
              background: '#ffffff',
              boxShadow: '0 24px 64px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06)',
            }}
          >
            {/* ── Header ── */}
            <div
              className="flex items-center justify-between px-4 py-3 flex-shrink-0 border-b border-slate-200 bg-slate-50"
            >
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="relative w-9 h-9 rounded-xl bg-white border border-primary/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(37,99,235,0.15)] overflow-hidden flex-shrink-0">
                  <motion.img
                    src={CHATBOT_ICON}
                    alt="Tuly AI"
                    className="w-7 h-7 object-contain"
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3, ease: 'easeInOut' }}
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-slate-50" />
                </div>
                <div>
                  <p className="text-slate-800 font-semibold text-sm leading-tight tracking-tight">Tuly AI</p>
                  <p className="text-slate-400 text-[11px] leading-none mt-0.5">
                    {lang === 'ar' ? 'مساعد ذكي · متاح الآن' : 'AI Assistant · Online now'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-0.5">
                <button
                  onClick={clearChat}
                  title={lang === 'ar' ? 'محادثة جديدة' : 'New chat'}
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-all"
                >
                  <Trash2 size={14} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-all"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* ── Messages ── */}
            <div
              className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50"
              style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.1) transparent' }}
            >
              {messages.map((msg) => {
                const isUser = msg.role === 'user';
                const rowDir = isUser
                  ? (isRTL ? 'flex-row'         : 'flex-row-reverse')
                  : (isRTL ? 'flex-row-reverse' : 'flex-row');

                return (
                  <motion.div
                    key={msg.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`flex gap-2 ${rowDir}`}
                  >
                    {/* AI avatar */}
                    {!isUser && (
                      <div className="w-6 h-6 rounded-lg bg-white border border-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden shadow-sm">
                        <img src={CHATBOT_ICON} alt="" className="w-5 h-5 object-contain" />
                      </div>
                    )}

                    <div className={`max-w-[85%] group flex flex-col ${isUser ? (isRTL ? 'items-start' : 'items-end') : 'items-start'}`}>
                      {/* Bubble */}
                      <div className={`px-3.5 py-2.5 rounded-2xl ${
                        isUser
                          ? `bg-gradient-to-br from-primary to-[#3A66FF] text-white ${isRTL ? 'rounded-tl-sm' : 'rounded-tr-sm'}`
                          : msg.isError
                            ? `bg-red-50 border border-red-200 text-red-600 ${isRTL ? 'rounded-tr-sm' : 'rounded-tl-sm'}`
                            : `bg-white border border-slate-200 text-slate-700 ${isRTL ? 'rounded-tr-sm' : 'rounded-tl-sm'}`
                      }`}>
                        {!isUser && !msg.isError
                          ? <MarkdownContent content={msg.content} />
                          : <span className="text-[0.8125rem] leading-relaxed">{msg.content}</span>
                        }
                      </div>

                      {/* Timestamp + copy */}
                      <div className={`flex items-center gap-2 mt-1 ${isUser ? (isRTL ? 'flex-row-reverse' : 'flex-row') : 'flex-row'}`}>
                        <span className="text-[10px] text-slate-400">{formatTime(msg.timestamp)}</span>
                        {!isUser && (
                          <button
                            onClick={() => copyMessage(msg.id, msg.content)}
                            className="text-slate-300 hover:text-slate-500 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            {copied === msg.id
                              ? <Check size={10} className="text-emerald-400" />
                              : <Copy size={10} />
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {/* Typing indicator */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <div className="w-6 h-6 rounded-lg bg-white border border-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-sm">
                    <img src={CHATBOT_ICON} alt="" className="w-5 h-5 object-contain" />
                  </div>
                  <div className={`px-4 py-3 rounded-2xl bg-white border border-slate-200 flex items-center gap-1.5 ${isRTL ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}>
                    {[0, 1, 2].map(i => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.8s' }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* ── Lead Capture ── */}
              <AnimatePresence>
                {showLead && !leadCaptured && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="rounded-2xl border border-primary/20 bg-primary/5 p-4"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-slate-700 text-xs font-semibold">
                          {lang === 'ar' ? '🎯 هل تريد المتابعة من فريقنا؟' : '🎯 Want our team to reach out?'}
                        </p>
                        <p className="text-slate-400 text-[11px] mt-0.5">
                          {lang === 'ar'
                            ? 'أدخل بريدك وسيتواصل معك خبراؤنا.'
                            : 'Leave your email — our experts will follow up.'}
                        </p>
                      </div>
                      <button onClick={() => setShowLead(false)} className="text-white/25 hover:text-white/50 transition-colors flex-shrink-0 mt-0.5">
                        <X size={13} />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={leadEmail}
                        onChange={e => setLeadEmail(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && submitLead()}
                        placeholder={lang === 'ar' ? 'بريدك الإلكتروني' : 'your@company.com'}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        className="flex-1 min-w-0 bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-700 text-xs placeholder:text-slate-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                      />
                      <button
                        onClick={submitLead}
                        disabled={!leadEmail.trim() || leadSaving}
                        className="px-3.5 py-2 rounded-xl bg-primary text-white text-xs font-semibold hover:bg-primary-hover transition-colors disabled:opacity-40 whitespace-nowrap"
                      >
                        {leadSaving ? '…' : (lang === 'ar' ? 'إرسال' : 'Send')}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </div>

            {/* ── Quick Prompts (shown only at start) ── */}
            {messages.length <= 1 && (
              <div className="px-4 pb-3 grid grid-cols-2 gap-1.5 flex-shrink-0 bg-slate-50 border-t border-slate-100 pt-3">
                {quickPrompts.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => sendMessage(p.label)}
                      className="flex items-start gap-2 text-left px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-primary/30 hover:bg-primary/[0.03] transition-all text-[11px] leading-snug group"
                    >
                      <Icon size={12} className="text-primary/60 group-hover:text-primary flex-shrink-0 mt-0.5 transition-colors" />
                      {p.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── Input ── */}
            <div className="border-t border-slate-100 px-3 py-3 flex items-end gap-2 flex-shrink-0 bg-white">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('assistant.placeholder')}
                rows={1}
                dir={isRTL ? 'rtl' : 'ltr'}
                className="flex-1 resize-none text-sm bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all max-h-24"
                style={{ minHeight: '40px' }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 96) + 'px';
                }}
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isLoading}
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
                  input.trim() && !isLoading
                    ? 'bg-primary text-white hover:bg-primary-hover shadow-[0_0_16px_rgba(37,99,235,0.4)]'
                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                }`}
              >
                <Send size={15} className={isRTL ? 'rotate-180' : ''} />
              </button>
            </div>

            {/* ── Footer ── */}
            <div className="px-4 py-2 flex items-center justify-between border-t border-slate-100 flex-shrink-0 bg-white">
              <span className="text-slate-300 text-[10px]">Powered by Tuly AI</span>
              <a
                href="/contact"
                className="flex items-center gap-1 text-primary/60 hover:text-primary text-[10px] font-medium transition-colors"
              >
                {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                <ArrowRight size={9} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
